/**
 * BoardStateEvaluator — Positional evaluation function for deep rollout search.
 *
 * Given a snapshot of game state, returns a score 0-100 representing how
 * healthy the position is. Analogous to a chess engine's static evaluation.
 *
 * Components:
 *  1. Progress (25%) — fraction of cubes cleared
 *  2. Stack Health (25%) — how close to overflow / game over
 *  3. Reachability (20%) — fraction of remaining cubes that are shootable
 *  4. Attacker Alignment (20%) — fraction of available attackers that have work to do
 *  5. Budget Health (10%) — per-color ammo sufficiency (0 if any color is impossible)
 */

import type { CellStack } from '../types/board';
import type { BoardSnapshot, BotAttacker, BotOrb } from './BotStrategy';
import { buildReachMap } from './BotStrategy';

// ── Public Interface ────────────────────────────────────────────────

export interface BoardStateSnapshot {
  board: BoardSnapshot;
  stack: BotAttacker[];
  queue: BotAttacker[][];   // per-lane arrays
  conveyor: BotAttacker[];
  maxStackSlots: number;
  totalCellsAtStart: number;
}

/** Detailed breakdown of each scoring component (for debugging / tuning). */
export interface EvalBreakdown {
  progress: number;          // 0-1
  stackHealth: number;       // 0-1
  reachability: number;      // 0-1
  attackerAlignment: number; // 0-1
  budgetHealth: number;      // 0-1 (0 = impossible color)
  raw: number;               // weighted sum before scaling to 0-100
}

// ── Weights ─────────────────────────────────────────────────────────

const W_PROGRESS           = 0.25;
const W_STACK_HEALTH       = 0.25;
const W_REACHABILITY       = 0.20;
const W_ATTACKER_ALIGNMENT = 0.20;
const W_BUDGET_HEALTH      = 0.10;

// ── Main Evaluation ─────────────────────────────────────────────────

/**
 * Evaluate a board position and return a score from 0-100.
 *  - 100 = board fully cleared (win)
 *  - 0 = stack overflow, or impossible position (a color has cubes but zero ammo)
 */
export function evaluateBoardState(state: BoardStateSnapshot): number {
  const { board, stack, queue, conveyor, maxStackSlots, totalCellsAtStart } = state;

  // Count remaining cells
  const remaining = countCells(board);

  // Instant win
  if (remaining === 0) return 100;

  // Stack overflow check — used slots vs max
  const stackUsed = stack.reduce((sum, a) => sum + a.size, 0);
  if (stackUsed > maxStackSlots) return 0;

  // ── 1. Progress ───────────────────────────────────────────────────
  const effectiveTotal = Math.max(totalCellsAtStart, 1);
  const progress = 1 - remaining / effectiveTotal;

  // ── 2. Stack Health ───────────────────────────────────────────────
  const stackHealth = maxStackSlots > 0 ? 1 - stackUsed / maxStackSlots : 1;

  // ── 3. Reachability ───────────────────────────────────────────────
  const reachMap = buildReachMap(board);
  let totalReachable = 0;
  for (const count of reachMap.values()) {
    totalReachable += count;
  }
  const reachability = remaining > 0 ? Math.min(1, totalReachable / remaining) : 1;

  // ── 4. Attacker Alignment ─────────────────────────────────────────
  // Gather all "available" attackers: stack + first few in each queue lane + conveyor.
  // Weight stack attackers 2x because they are the immediate overflow threat.
  const QUEUE_PEEK_DEPTH = 3;

  let productiveWeighted = 0;
  let totalWeighted = 0;

  // Helper: is an orb productive (has reachable cells)?
  const isOrbProductive = (orb: BotOrb): boolean =>
    orb.ammo > 0 && (reachMap.get(orb.colorIndex) ?? 0) > 0;

  const isAttackerProductive = (att: BotAttacker): boolean =>
    att.orbs.some(isOrbProductive);

  // Stack (weight 2x)
  for (const att of stack) {
    totalWeighted += 2;
    if (isAttackerProductive(att)) productiveWeighted += 2;
  }

  // Conveyor (weight 1x)
  for (const att of conveyor) {
    totalWeighted += 1;
    if (isAttackerProductive(att)) productiveWeighted += 1;
  }

  // Queue: first QUEUE_PEEK_DEPTH per lane (weight 1x, deduplicated)
  const seenAttackers = new Set<BotAttacker>();
  for (const lane of queue) {
    for (let i = 0; i < Math.min(lane.length, QUEUE_PEEK_DEPTH); i++) {
      const att = lane[i];
      if (!att || seenAttackers.has(att)) continue;
      seenAttackers.add(att);
      totalWeighted += 1;
      if (isAttackerProductive(att)) productiveWeighted += 1;
    }
  }

  const attackerAlignment = totalWeighted > 0 ? productiveWeighted / totalWeighted : 0;

  // ── 5. Budget Health ──────────────────────────────────────────────
  // For each color on the board, check total ammo across ALL remaining attackers.
  const colorCellCounts = countColorCells(board);
  const colorAmmoBudget = buildAmmoBudget(stack, queue, conveyor);

  let budgetSum = 0;
  let budgetColors = 0;
  let impossible = false;

  for (const [colorIndex, cellCount] of colorCellCounts) {
    const ammo = colorAmmoBudget.get(colorIndex) ?? 0;
    if (ammo === 0) {
      impossible = true;
      break;
    }
    budgetSum += Math.min(1.0, ammo / cellCount);
    budgetColors++;
  }

  if (impossible) return 0;

  const budgetHealth = budgetColors > 0 ? budgetSum / budgetColors : 1;

  // ── Weighted Sum ──────────────────────────────────────────────────
  const raw =
    W_PROGRESS * progress +
    W_STACK_HEALTH * stackHealth +
    W_REACHABILITY * reachability +
    W_ATTACKER_ALIGNMENT * attackerAlignment +
    W_BUDGET_HEALTH * budgetHealth;

  return Math.round(raw * 100);
}

/**
 * Same as evaluateBoardState but also returns the per-component breakdown.
 */
export function evaluateBoardStateDetailed(state: BoardStateSnapshot): { score: number; breakdown: EvalBreakdown } {
  const { board, stack, queue, conveyor, maxStackSlots, totalCellsAtStart } = state;

  const remaining = countCells(board);

  if (remaining === 0) {
    return {
      score: 100,
      breakdown: { progress: 1, stackHealth: 1, reachability: 1, attackerAlignment: 1, budgetHealth: 1, raw: 1 },
    };
  }

  const stackUsed = stack.reduce((sum, a) => sum + a.size, 0);
  if (stackUsed > maxStackSlots) {
    return {
      score: 0,
      breakdown: { progress: 0, stackHealth: 0, reachability: 0, attackerAlignment: 0, budgetHealth: 0, raw: 0 },
    };
  }

  const effectiveTotal = Math.max(totalCellsAtStart, 1);
  const progress = 1 - remaining / effectiveTotal;

  const stackHealth = maxStackSlots > 0 ? 1 - stackUsed / maxStackSlots : 1;

  const reachMap = buildReachMap(board);
  let totalReachable = 0;
  for (const count of reachMap.values()) {
    totalReachable += count;
  }
  const reachability = remaining > 0 ? Math.min(1, totalReachable / remaining) : 1;

  const QUEUE_PEEK_DEPTH = 3;
  let productiveWeighted = 0;
  let totalWeighted = 0;
  const isOrbProductive = (orb: BotOrb): boolean =>
    orb.ammo > 0 && (reachMap.get(orb.colorIndex) ?? 0) > 0;
  const isAttackerProductive = (att: BotAttacker): boolean =>
    att.orbs.some(isOrbProductive);

  for (const att of stack) {
    totalWeighted += 2;
    if (isAttackerProductive(att)) productiveWeighted += 2;
  }
  for (const att of conveyor) {
    totalWeighted += 1;
    if (isAttackerProductive(att)) productiveWeighted += 1;
  }
  const seenAttackers = new Set<BotAttacker>();
  for (const lane of queue) {
    for (let i = 0; i < Math.min(lane.length, QUEUE_PEEK_DEPTH); i++) {
      const att = lane[i];
      if (!att || seenAttackers.has(att)) continue;
      seenAttackers.add(att);
      totalWeighted += 1;
      if (isAttackerProductive(att)) productiveWeighted += 1;
    }
  }
  const attackerAlignment = totalWeighted > 0 ? productiveWeighted / totalWeighted : 0;

  const colorCellCounts = countColorCells(board);
  const colorAmmoBudget = buildAmmoBudget(stack, queue, conveyor);

  let budgetSum = 0;
  let budgetColors = 0;
  let impossible = false;

  for (const [colorIndex, cellCount] of colorCellCounts) {
    const ammo = colorAmmoBudget.get(colorIndex) ?? 0;
    if (ammo === 0) {
      impossible = true;
      break;
    }
    budgetSum += Math.min(1.0, ammo / cellCount);
    budgetColors++;
  }

  if (impossible) {
    return {
      score: 0,
      breakdown: { progress, stackHealth, reachability, attackerAlignment, budgetHealth: 0, raw: 0 },
    };
  }

  const budgetHealth = budgetColors > 0 ? budgetSum / budgetColors : 1;

  const raw =
    W_PROGRESS * progress +
    W_STACK_HEALTH * stackHealth +
    W_REACHABILITY * reachability +
    W_ATTACKER_ALIGNMENT * attackerAlignment +
    W_BUDGET_HEALTH * budgetHealth;

  return {
    score: Math.round(raw * 100),
    breakdown: { progress, stackHealth, reachability, attackerAlignment, budgetHealth, raw },
  };
}

// ── Helpers ─────────────────────────────────────────────────────────

function countCells(board: BoardSnapshot): number {
  let count = 0;
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      count += board.grid[r][c].length;
    }
  }
  for (const mb of board.megaBlocks.values()) {
    count += mb.hp;
  }
  return count;
}

/** Count remaining cells per color on the board. */
function countColorCells(board: BoardSnapshot): Map<number, number> {
  const counts = new Map<number, number>();
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      for (const ci of board.grid[r][c]) {
        counts.set(ci, (counts.get(ci) ?? 0) + 1);
      }
    }
  }
  for (const mb of board.megaBlocks.values()) {
    counts.set(mb.colorIndex, (counts.get(mb.colorIndex) ?? 0) + mb.hp);
  }
  return counts;
}

/** Sum total ammo per color across all available attackers. */
function buildAmmoBudget(
  stack: BotAttacker[],
  queue: BotAttacker[][],
  conveyor: BotAttacker[],
): Map<number, number> {
  const budget = new Map<number, number>();

  const addOrbs = (orbs: BotOrb[]) => {
    for (const orb of orbs) {
      if (orb.ammo > 0) {
        budget.set(orb.colorIndex, (budget.get(orb.colorIndex) ?? 0) + orb.ammo);
      }
    }
  };

  for (const att of stack) addOrbs(att.orbs);
  for (const att of conveyor) addOrbs(att.orbs);

  // All queue attackers (not just fronts — they all eventually come through)
  const seen = new Set<BotAttacker>();
  for (const lane of queue) {
    for (const att of lane) {
      if (!att || seen.has(att)) continue;
      seen.add(att);
      addOrbs(att.orbs);
    }
  }

  return budget;
}
